import { HugeiconsIcon } from "@hugeicons/react"
import { DeliveryBox01Icon, Cancel01Icon, PackageIcon } from "@hugeicons/core-free-icons"
import { Link } from "react-router-dom"
import { cn } from "@/lib/utils"
import type { ChatReference } from "../types/assistant.types"

interface ReferenceChipProps {
  reference: ChatReference
  onRemove?: () => void
  /** Render as a non-removable inline chip inside a transcript bubble. */
  inline?: boolean
}

/** Compact chip showing an order or line-item reference.
 *
 *  - Inline transcript chips link to the underlying entity.
 *  - Composer chips (with ``onRemove``) show a dismiss "x".
 *  - ``auto: true`` chips (the URL-derived "Order #123" prepopulated
 *    when the chat opens on an order page) get a softer ring so users
 *    can tell at a glance they were added by the system. */
export function ReferenceChip({ reference, onRemove, inline = false }: ReferenceChipProps) {
  const isLineItem = reference.type === "line_item"
  const icon = isLineItem ? PackageIcon : DeliveryBox01Icon
  const href =
    reference.type === "order"
      ? `/orders/${reference.id}`
      : `/orders/${reference.order_id}`

  const body = (
    <>
      <HugeiconsIcon
        icon={icon}
        size={12}
        className="shrink-0 text-muted-foreground"
      />
      <span className="truncate text-xs" title={reference.label}>
        {reference.label}
      </span>
    </>
  )

  if (inline) {
    // Inline chips render inside both the user bubble (``bg-primary``,
    // white text) and the assistant bubble (``bg-muted``, dark text).
    // Force a contrasting card-style chip with explicit ``text-foreground``
    // so the label is always readable — without that, white text from
    // the user bubble would inherit through and disappear against the
    // chip's light background.
    return (
      <Link
        to={href}
        className="inline-flex items-center gap-1 rounded-md bg-background px-1.5 py-0.5 text-xs text-foreground ring-1 ring-border hover:bg-accent hover:text-accent-foreground"
      >
        {body}
      </Link>
    )
  }

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 rounded-md border bg-background py-1 ring-1 ring-border",
        onRemove ? "pr-0.5 pl-2" : "px-2",
        reference.auto && "ring-primary/30",
      )}
      title={reference.label}
    >
      {body}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
          title="Remove"
        >
          <HugeiconsIcon icon={Cancel01Icon} size={11} />
        </button>
      )}
    </div>
  )
}
